import React, { useEffect, useState, useRef, useCallback } from "react";
import { useHistory, useLocation } from "react-router";
import { useSelector } from "react-redux";
import { Form, Input, InputNumber, Select, Radio, Button, Alert, Card, Empty, Spin, Modal, Tag, Upload, Tabs, Checkbox } from "antd";
import { ArrowLeftOutlined, FileImageOutlined, AppstoreOutlined, CheckOutlined, SearchOutlined, MailOutlined, UploadOutlined, CameraOutlined, SnippetsOutlined, CloseOutlined, FilePdfOutlined, FileExcelOutlined, VideoCameraOutlined, PaperClipOutlined } from "@ant-design/icons";
import Loader from "components/Loader/Loader";
import addNotification from "helpers/notify";
import { createIssue, saveIssueMarkup, uploadIssueAttachments, getProjectModelSources, setIssueSourceModel, getIssueTags, declineExtraScopeDrawing } from "api/issue/issueApi";
import { askForShopDrawing } from "components/Coordination/ExtraScope";
import { getDocControl } from "api/docControl/getDocControl";
import { rememberSurface } from "helpers/issueSurface";
import { mediaKindOf, MEDIA_ACCEPT, MEDIA_ALLOWED_TEXT } from "components/Issue/IssueSourceControls";
import { renderPdfThumbnail } from "helpers/pdfPreview";
import { getRememberedProject, rememberProject } from "helpers/projectSelection";
import { isModelUploading } from "helpers/modelVisibility";
import { getParticipantsRoster } from "api/participants/participantsApi";
import { listGanttItems } from "api/gantt/ganttApi";
import { pairableDrawings } from "api/issueDrawingPair";
import { withActivityPaths } from "helpers/schedule/activityLabel";
import { leafActivityOptions, LEAF_SELECT_PROPS } from "helpers/schedule/leafActivityOptions";
import { getUsers } from "api/users/getUsers";
import { useHttp } from "hooks/useHttp";
import { eventTrack } from "helpers/ga4Helper";
import { url, tokenRef } from "api";
import WebGlApp from "webgl";
import { getCompressedJsonFile } from "api/files/getJsonFile";
import { generateRawMeshes, generateRawTree } from "helpers/three";
import { captureOpenedAtSnapshot } from "helpers/openedAtSnapshot";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import "./CreateIssuePage.scss";
import { issueLabel } from "helpers/artifactPrefix";
import LocationPicker from "components/Coordination/LocationPicker";
import DisciplineContractorPicker from "components/Issue/DisciplineContractorPicker";
import { getDiscipline } from "api/discipline/getDiscipline";
import ViewerEffectsButton from "components/Viewer/ViewerEffectsButton";
import ViewerViewButtons from "components/Viewer/ViewerViewButtons";
import ViewerToolbar from "components/Viewer/ViewerToolbar";
import PeoplePicker from "components/PeoplePicker/PeoplePicker";
import { matchOption } from "components/SearchSelect/searchSelect";
import { attachBuildingGrid } from "helpers/buildingGridLoad";

const { Option } = Select;
const { TextArea } = Input;

// Server-relative screenshot/viewpoint paths ("/uploads/…") need the API origin.
// The field's own value shapes (components/PeoplePicker): a responsible is a
// user id, or `email:<addr>` for a roster contact with no account; a
// deliverable recipient is an address.
const responsiblePersonValue = (p) => p.userId || (p.email ? `email:${p.email}` : "");
const recipientPersonValue = (p) => p.email;

const previewSrc = (s) => (s && s.startsWith("/") ? `${url}${s}` : s);

export default function CreateIssuePage() {
  const history = useHistory();
  const location = useLocation();
  const [form] = Form.useForm();
  const userInfoRedux = useSelector((s) => s.user.userInfo);

  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  // Tags already in use, so a second issue in the same batch is picked
  // rather than retyped (and therefore cannot be misspelled into its own
  // one-issue filter). `tagPreview` shows the composed title as you type.
  const [tagOptions, setTagOptions] = useState([]);
  const [tagPreview, setTagPreview] = useState("");
  // Discipline / contractor (components/Issue/DisciplineContractorPicker).
  const [createPick, setCreatePick] = useState(null);
  const [createDisciplines, setCreateDisciplines] = useState([]);
  const myCompany = userInfoRedux?.company;
  useEffect(() => {
    const company = myCompany;
    if (!company) { setCreateDisciplines([]); return undefined; }
    let alive = true;
    getDiscipline(company)
      .then((d) => { if (alive) setCreateDisciplines(Array.isArray(d) ? d : (d?.data || [])); })
      .catch(() => { if (alive) setCreateDisciplines([]); });
    return () => { alive = false; };
  }, [myCompany]);
  const [docControlSheets, setDocControlSheets] = useState([]);
  // Source is OPTIONAL and single-select (2026-08-18): the three source
  // buttons on the left mirror the three tabs of the source panel -
  //   '2d'         Sheets (default tab)         -> doc-control sheet
  //   'screenshot' Paste media from clipboard   -> image + attachments
  //   '3d'         3D model (+ pushed viewpoints)
  // 'none' = nothing chosen (comment-only issue, sourceType 'blank'). A
  // selected sheet is kept while the 3D tab is active -> hybrid issue.
  const [sourceType, setSourceType] = useState("2d"); // '2d' | 'screenshot' | '3d' | 'none'

  // PAIR THIS ISSUE WITH AN UNDELIVERED SHOP DRAWING - one piece of work, one
  // set of hours (owner, 2026-10-08: "in an issue during creation, we should
  // have the option to select a shopdrawing request that is not delivered,
  // when we select sheet delivery option").
  //
  // OFFERED ON THE SHEETS SOURCE, which is what "sheet delivery" is here: the
  // issue is about a drawing, so the drawing it belongs with is the question.
  // The list comes from the server (only undelivered, only unpaired) rather
  // than being filtered in the browser - a client-side filter would be a
  // second opinion about what may be paired, and the server would refuse the
  // pair after the person had already chosen it.
  const [pairedDrawingId, setPairedDrawingId] = useState(null);
  const [pairable, setPairable] = useState([]);
  const [activeTab, setActiveTab] = useState("sheets"); // 'sheets' | 'media' | '3d'
  const TAB_FOR = { "2d": "sheets", screenshot: "media", "3d": "3d" };
  const SOURCE_FOR = { sheets: "2d", media: "screenshot", "3d": "3d" };
  const pickSource = (type) => {
    // Clicking the active source again deselects it (comment-only issue);
    // the panel stays on the same tab so nothing jumps.
    if (sourceType === type) { setSourceType("none"); return; }
    setSourceType(type);
    setActiveTab(TAB_FOR[type]);
  };
  // EVERY sheet picked, in the order they were picked. Markup is drawn on
  // one sheet at a time, so the FIRST pick is the active source
  // (`selectedSheet` below - what the issue opens on) and the rest ride
  // along on the issue so the resolve page can switch between them.
  const [selectedSheets, setSelectedSheets] = useState([]);
  const selectedSheet = selectedSheets[0] || null;
  // Non-image media pasted / dropped in the "Paste media" tab (video, PDF,
  // Excel/CSV): attached to the issue right after it is created.
  const [pendingMedia, setPendingMedia] = useState([]);
  // DOCUMENTS ATTACHED FROM THE SHEETS TAB (owner, 2026-10-16: "add an option
  // to directly upload multiple documents, that will be used as attachment
  // for issue creation ... on the sheet tab"). Uploaded once the issue
  // exists, as attachments only - the sheet stays the issue's picture.
  const [attachDocs, setAttachDocs] = useState([]);

  // Screenshot / image source (paste or upload → /issue/proposals/screenshot)
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  // Every pasted / uploaded image, oldest first ({url, at}). The last one is
  // the default image; on save the earlier ones become the issue's image
  // history (versions with their capture time) so nothing pasted is lost.
  const [screenshotHistory, setScreenshotHistory] = useState([]);
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);

  // Viewpoints pushed by the Revit / Navisworks plugins for this project
  const [viewpoints, setViewpoints] = useState([]);
  const [loadingViewpoints, setLoadingViewpoints] = useState(false);
  const [selectedViewpoint, setSelectedViewpoint] = useState(null);
  const [error, setError] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedResponsibleUsers, setSelectedResponsibleUsers] = useState([]);
  // WHO GETS THE DELIVERABLE when this issue is approved - optional, and a
  // different list from the responsible users above. Tag mode, so anybody
  // can be one: on the roster, or an address typed in that nobody has.
  const [selectedRecipients, setSelectedRecipients] = useState([]);

  // Invite-by-email (responsible person) modal state
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  // Existing users surfaced via invite that aren't in companyUsers yet
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [participantRoster, setParticipantRoster] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [ganttItems, setGanttItems] = useState([]);

  const submitInvite = async () => {
    const email = inviteEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      addNotification("Enter a valid email address", "warning");
      return;
    }
    if (!selectedProject) {
      addNotification("Select a project first", "warning");
      return;
    }
    setInviting(true);
    try {
      // Registered on the project roster → plain responsible user.
      // Anyone else is added by EMAIL: POST /issue resolves it server-side
      // (existing account → responsibleUsers, otherwise a no-account
      // guest contact who receives a personal no-login link). No
      // registration invite is sent any more.
      const lower = email.toLowerCase();
      const known = participantRoster
        .flatMap((c) => c.users || [])
        .find((u) => u.userId && String(u.email || "").toLowerCase() === lower);
      if (known) {
        setSelectedResponsibleUsers((prev) =>
          prev.includes(String(known.userId)) ? prev : [...prev, String(known.userId)]
        );
        addNotification("They already have an account — added to responsible users", "success");
      } else {
        const key = `email:${lower}`;
        setInvitedUsers((prev) =>
          prev.some((u) => u._id === key) ? prev : [...prev, { _id: key, email: lower, contact: true }]
        );
        setSelectedResponsibleUsers((prev) => (prev.includes(key) ? prev : [...prev, key]));
        addNotification(
          "Added as responsible — they'll get a personal no-login link when the issue is created",
          "success"
        );
      }
      setInviteModalOpen(false);
      setInviteEmail("");
    } catch (e) {
      addNotification(e.message || "Could not add responsible", "danger");
    } finally {
      setInviting(false);
    }
  };

  // 3D Viewer refs and state
  const viewerRef = useRef(null);
  const WebGL = useRef(null);
  const [loading3D, setLoading3D] = useState(false);
  const [modelLoaded, setModelLoaded] = useState(false);
  // 3D source picker (same as the resolve page SOURCE bar): the project's
  // latest export, one of its MODEL PUSHES (every sync the plugin made -
  // what the coordination side calls a revision), one of its model
  // versions (Timelines + Revision - each revision loads ITS OWN files)
  // or the elements exported against an issue from Revit / Navisworks.
  // Rendered in the streaming viewer.
  //
  // A push differs from the other three in what it carries: no zipInfo,
  // just the versioned manifest its export was written to. So it streams
  // (never the legacy GLB list) and the issue is attached to it AFTER
  // creation, through PUT /issue/:id/source-model { commitId } - the same
  // call the revision rail makes, and the only place that validates the
  // push still has geometry.
  const [modelSources, setModelSources] = useState({ commits: [], timelines: [], issues: [], loading: false });
  const [modelSourceKey, setModelSourceKey] = useState("project"); // 'project' | 'commit:<id>' | 'timeline:<id>' | 'issue:<id>'
  useEffect(() => {
    let alive = true;
    if (!selectedProject) { setModelSources({ commits: [], timelines: [], issues: [], loading: false }); return undefined; }
    setModelSources((m) => ({ ...m, loading: true }));
    getProjectModelSources(selectedProject)
      .then((r) => {
        if (!alive) return;
        // Only pushes that still have geometry: their own versioned
        // export, or being the live model. The rest are history.
        setModelSources({
          commits: (r?.commits || []).filter((c) => c.streamable),
          timelines: r?.timelines || [], issues: r?.issues || [], loading: false,
        });
      })
      .catch(() => { if (alive) setModelSources({ commits: [], timelines: [], issues: [], loading: false }); });
    setModelSourceKey("project");
    return () => { alive = false; };
  }, [selectedProject]);
  const isMountedRef = useRef(true);

  // Track which projects have 2D documents
  const [projectDocCounts, setProjectDocCounts] = useState({});

  // Track sheet thumbnails
  const [sheetThumbnails, setSheetThumbnails] = useState({});

  // Search state for sheets
  const [sheetSearchQuery, setSheetSearchQuery] = useState("");

  // Fetch projects: GET /projects/picker (2026-08-18). The heavy GET
  // /projects silently drops every project without a ProjectExportSettings
  // doc (coordination-created projects such as spaceX-test never got one),
  // so they were missing here. The picker lists every project the user is
  // on; the full record (zipInfo for the 3D model) is fetched on selection.
  const { loading: projectsLoading, request: getProjectsAPI } = useHttp({
    requestCallback: () => fetch(`${url}/projects/picker`, {
      headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
    }).then(async (r) => { const b = await r.json().catch(() => ({})); if (!r.ok) throw new Error(b.err || "Failed to load projects"); return b; }),
    onLoad: (res) => {
      console.log("✅ Projects loaded:", res);
      const projectsList = (res?.projects || []).map((p) => ({ ...p, _light: true }));
      setProjects(projectsList);

      // Log which projects have 3D models (check zipInfo with GLB files)
      const projectsWith3D = projectsList.filter(p => {
        if (!p.zipInfo || !p.zipInfo.paths) return false;
        return p.zipInfo.paths.some(path => path.endsWith(".glb") || path.endsWith(".glb.gz"));
      });
      console.log(`📊 Total projects: ${projectsList.length}`);
      console.log(`🎨 Projects with 3D models: ${projectsWith3D.length}`);
      if (projectsWith3D.length > 0) {
        console.log("🎨 Projects with 3D:", projectsWith3D.map(p => p.title));
      }
    },
    onError: (err) => {
      console.error("❌ Failed to load projects:", err);
      addNotification("Failed to load projects", "danger");
    },
  });

  // Fetch doc control sheets - following DocControl pattern
  const { loading: loadingSheets, request: fetchDocControlSheets } = useHttp({
    requestCallback: (projectId) => {
      if (!projectId) {
        console.warn("⚠️ No projectId provided to fetchDocControlSheets");
        return Promise.resolve({ documents: [] });
      }
      // Project-scoped Document Control list (same source as the issue
      // page's SHEET picker): every sheet of the project incl. the "Issues"
      // category, not just the caller's company upload.
      return fetch(`${url}/issue/project/${projectId}/sheets`, {
        headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
      }).then(async (r) => { const b = await r.json().catch(() => ({})); if (!r.ok) throw new Error(b.err || "Failed to load sheets"); return b; });
    },
    onLoad: (res) => {
      console.log("✅ Doc control sheets loaded:", res);
      const sheets = res?.sheets || res?.documents || [];
      console.log(`📄 Setting ${sheets.length} sheets to state`);
      setDocControlSheets(sheets);
      if (sheets.length > 0) {
        console.log(`📄 Loaded ${sheets.length} sheets for display`);
        console.log(`📄 First sheet:`, sheets[0]?.sheetName, sheets[0]?.link);
      } else {
        console.log("⚠️ No documents found for this project");
      }
    },
    onError: (err) => {
      console.error("❌ Failed to load sheets:", err);
      addNotification("Failed to load 2D sheets", "danger");
      setDocControlSheets([]);
    },
  });

  useEffect(() => {
    console.log("CreateIssuePage mounted, fetching projects...");
    getProjectsAPI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // The tags already in use, scoped to the chosen project. Reloaded when
  // the project changes because a tag belongs to a job, not to a company.
  useEffect(() => {
    let cancelled = false;
    // A tag belongs to a project, so nothing is suggested until one is
    // chosen: offering another job's labels here is how a tag spreads
    // across work that has nothing in common. Typing is never blocked.
    form.setFieldsValue({ tags: [] });   // the old project's labels do not follow
    setCreatePick(null);
    setTagPreview("");
    if (!selectedProject) { setTagOptions([]); return undefined; }
    getIssueTags(selectedProject)
      .then((r) => {
        if (cancelled) return;
        setTagOptions((r?.tags || []).map((t) => ({
          value: t.tag,
          label: `${t.tag}  ·  ${t.count} issue${t.count === 1 ? "" : "s"}`,
        })));
      })
      .catch(() => { if (!cancelled) setTagOptions([]); });
    return () => { cancelled = true; };
  }, [selectedProject]);

  // Fetch ALL documents once and count by project - following DocControl pattern
  useEffect(() => {
    const fetchAllDocuments = async () => {
      if (!projects || projects.length === 0) return;

      console.log("📊 Fetching all documents for company...");

      try {
        // Fetch all documents for the company - same pattern as DocControl.jsx
        const response = await getDocControl({
          sort: "updatedAt",
          sort_dir: -1,
        });

        console.log("✅ All documents loaded:", response);

        // Count documents by project
        const counts = {};
        if (response?.documents) {
          response.documents.forEach((doc) => {
            const projectId = doc.projectId?._id || doc.projectId;
            if (projectId) {
              counts[projectId] = (counts[projectId] || 0) + 1;
            }
          });
        }

        console.log("📄 Document counts by project:", counts);
        console.log("📄 Projects with 2D documents:", Object.keys(counts).length);
        setProjectDocCounts(counts);
      } catch (err) {
        console.error("❌ Failed to fetch documents:", err);
      }
    };

    fetchAllDocuments();
  }, [projects]);

  // Fetch company users
  useEffect(() => {
    let isMounted = true; // Track if component is mounted

    const fetchCompanyUsers = async () => {
      if (!userInfoRedux?.company) return;

      if (isMounted) setLoadingUsers(true);

      try {
        const response = await getUsers(userInfoRedux.company);
        console.log("Company users response:", response);

        if (!isMounted) return; // Don't update state if unmounted

        if (response && Array.isArray(response)) {
          setCompanyUsers(response);
        } else if (response?.users && Array.isArray(response.users)) {
          setCompanyUsers(response.users);
        } else {
          setCompanyUsers([]);
        }
      } catch (err) {
        console.error("Error fetching company users:", err);
        if (isMounted) {
          addNotification("Failed to load company users", "warning");
          setCompanyUsers([]);
        }
      } finally {
        if (isMounted) setLoadingUsers(false);
      }
    };

    fetchCompanyUsers();

    return () => {
      isMounted = false; // Cleanup: mark as unmounted
    };
  }, [userInfoRedux?.company]);

  // Fetch the cross-company participant roster + plan items when a
  // project is selected (grouped responsible-users dropdown + plan link)
  useEffect(() => {
    let isMounted = true;

    if (!selectedProject) {
      setParticipantRoster([]);
      setGanttItems([]);
      return undefined;
    }

    setLoadingRoster(true);
    getParticipantsRoster(selectedProject)
      .then((res) => {
        if (isMounted) setParticipantRoster(Array.isArray(res) ? res : []);
      })
      .catch((err) => {
        console.error("Failed to load participant roster:", err);
        if (isMounted) setParticipantRoster([]);
      })
      .finally(() => {
        if (isMounted) setLoadingRoster(false);
      });

    listGanttItems(selectedProject)
      .then((res) => {
        if (isMounted) {
          const all = Array.isArray(res) ? res : res?.items || [];
          // A disabled activity was called off - the server refuses new
          // issues on one, so it must not be offered here either.
          // The WHOLE schedule is kept (a disabled row is still somebody's
          // parent); the picker offers only live leaves (leafActivityOptions).
          setGanttItems(withActivityPaths(all));
        }
      })
      .catch((err) => {
        console.error("Failed to load plan items:", err);
        if (isMounted) setGanttItems([]);
      });

    // A previously chosen plan item belongs to the previous project
    form.setFieldsValue({ ganttItemId: undefined });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  // Fetch doc control sheets when project changes - following DocControl pattern
  useEffect(() => {
    if (selectedProject) {
      console.log("🔄 Project changed, fetching sheets...");
      console.log("📌 Selected project:", selectedProject);
      fetchDocControlSheets(selectedProject);
    } else {
      // Clear sheets when no project selected
      console.log("🧹 No project selected, clearing sheets");
      setDocControlSheets([]);
      setSelectedSheets([]);
    }
  }, [selectedProject]); // ✅ Only selectedProject - fetchDocControlSheets is stable from useHttp

  // THE PAIRABLE SHOP DRAWINGS for this project: undelivered and not already
  // paired to another issue. The server decides which those are - a filter in
  // the browser would be a second opinion about what may be paired, and the
  // server would refuse the pair after the person had already chosen it.
  //
  // Cleared with the project, so a selection cannot survive onto a project
  // where that drawing does not exist.
  useEffect(() => {
    if (!selectedProject) { setPairable([]); setPairedDrawingId(null); return undefined; }
    let cancelled = false;
    pairableDrawings(selectedProject)
      .then((d) => { if (!cancelled) setPairable((d && d.drawings) || []); })
      .catch(() => { if (!cancelled) setPairable([]); });
    return () => { cancelled = true; };
  }, [selectedProject]);

  // Load 3D model when project with model is selected - following TimelineViewer pattern.
  // Lazy (2026-08-18): the model is loaded the first time the 3D tab is in
  // focus for the selected project (its container only exists inside that
  // tab pane) and kept while other tabs are shown.
  const loaded3DFor = useRef(null);
  const activeModelRef = useRef(null);
  // The title this page filled in by itself, so a later project switch
  // can tell "still the default" from "the user wrote this".
  const autoTitleRef = useRef("");
  const activeModelKey = activeModelRef.current?.key || "";
  useEffect(() => {
    const load3DModel = async () => {
      const project = projects.find(p => p._id === selectedProject);
      const model = activeModelRef.current;
      if (activeTab !== "3d") return;
      const key = model?.key || `none:${selectedProject}`;
      if (WebGL.current && loaded3DFor.current === key) return;
      loaded3DFor.current = key;

      // Cleanup previous WebGL instance
      if (WebGL.current) {
        console.log("🧹 Cleaning up previous WebGL instance");
        try {
          if (WebGL.current.loader && WebGL.current.loader.dispose) WebGL.current.loader.dispose();
          if (WebGL.current.viewer && WebGL.current.viewer.destroy) {
            WebGL.current.viewer.destroy();
          }
          if (WebGL.current.viewer && WebGL.current.viewer.renderer) {
            WebGL.current.viewer.renderer.dispose();
          }
        } catch (cleanupErr) {
          console.warn("Cleanup warning:", cleanupErr);
        }
        WebGL.current = null;
        setModelLoaded(false);
        setLoading3D(false);

        // Clear the viewer container to remove old canvas elements
        if (viewerRef.current) {
          while (viewerRef.current.firstChild) {
            viewerRef.current.removeChild(viewerRef.current.firstChild);
          }
          console.log("🧹 Cleared viewer container");
        }

        // Allow cleanup to complete before loading new model
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // The chosen 3D source. A project export / model version / issue
      // export carries its own zipInfo; a MODEL PUSH carries only the
      // manifest of the export it was written to, and streams from that.
      if (!project || !model || !viewerRef.current) {
        console.log("⚠️ No 3D model to load (no source)");
        return;
      }
      if (!model.manifestPath && !model.zipInfo?.paths) {
        console.log("⚠️ No 3D model to load (no zipInfo and no manifest)");
        return;
      }

      try {
        console.log("🎨 Loading 3D model:", model.name, model.kind);
        setLoading3D(true);

        // Re-add the hidden snapshot element after clearing
        if (viewerRef.current && !document.getElementById('take-snapshot')) {
          const snapshotLink = document.createElement('a');
          snapshotLink.id = 'take-snapshot';
          snapshotLink.style.display = 'none';
          viewerRef.current.appendChild(snapshotLink);
        }

        // GLB URLs of THIS export + its own manifest when it was streamed
        // (never the project-level manifest - a revision must show its own
        // model version). Streaming viewer with legacy GLB fallback.
        const baseURL = url;
        const zipPaths = model.zipInfo?.paths || [];
        const glbUrls = zipPaths
          .filter((path) => /\.glb(\.gz)?$/i.test(String(path)))
          .map((path) => `${baseURL}/${String(path).replace(/^\/+/, "")}`);
        const manifestPath = model.manifestPath || zipPaths.find((p) => /(^|\/)manifest\.json(\.gz)?$/i.test(String(p))) || "";
        const manifestUrl = manifestPath ? `${baseURL}/${String(manifestPath).replace(/^\/+/, "")}` : null;

        if (glbUrls.length === 0 && !manifestUrl) {
          console.log("⚠️ No viewable files in this export");
          setLoading3D(false);
          return;
        }

        WebGL.current = new WebGlApp(
          viewerRef.current,
          manifestUrl ? { glbs: glbUrls, manifestUrl } : { glbs: glbUrls },
          {
            setLoading: (loading) => {
              if (isMountedRef.current) {
                setLoading3D(loading);
                // When loading completes, mark model as loaded
                if (!loading) {
                  setModelLoaded(true);
                  console.log("✅ 3D model loaded successfully!");
                }
              }
            },
            setError: (err) => {
              console.error("❌ WebGL error:", err);
              addNotification("Failed to load 3D model", "danger");
              if (isMountedRef.current) {
                setLoading3D(false);
                setModelLoaded(false);
              }
            },
            setPercentsLoaded: (percent) => {
              // Progress is reported per GLB file, so it can exceed 100% with multiple files
              // Just log it without showing to user to avoid confusion
              if (percent <= 100) {
                console.log(`📊 Loading progress: ${percent}%`);
              }
            },
            setAllowToResize: () => {}, // Required by Loader
            setModal: () => {}, // Required by Loader
            setIsPopOver: () => {}, // Required by Viewer hover events
            onHoverMesh: () => {}, // Required by Viewer hover events
            setCoordsForPopover: () => {}, // Required by Viewer
            findInJsonUsingViewer: () => {}, // Required by Viewer
            setClickedChild: () => {}, // Required by Viewer selection
            selectedMesh: { current: null }, // Required by Viewer
            project: project, // Project reference
          },
          null, // line
          false, // socketviewer
          !!model.isIfcProject
        );
        attachBuildingGrid(WebGL.current.viewer, selectedProject);

        console.log("🎨 3D model initialization started...");
      } catch (err) {
        console.error("❌ Failed to load 3D model:", err);
        addNotification("Failed to load 3D model", "danger");
        if (isMountedRef.current) {
          setModelLoaded(false);
          setLoading3D(false);
        }
      }
    };

    if (selectedProject && viewerRef.current) {
      load3DModel();
    }

    // Cleanup on unmount
    return () => {
      if (WebGL.current) {
        console.log("🧹 Cleaning up WebGL on unmount");
        try {
          if (WebGL.current.loader && WebGL.current.loader.dispose) WebGL.current.loader.dispose();
          if (WebGL.current.viewer && WebGL.current.viewer.destroy) {
            WebGL.current.viewer.destroy();
          }
          if (WebGL.current.viewer && WebGL.current.viewer.renderer) {
            WebGL.current.viewer.renderer.dispose();
          }
        } catch (cleanupErr) {
          console.warn("Cleanup warning on unmount:", cleanupErr);
        }
        WebGL.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, projects, activeTab, activeModelKey, modelSourceKey, modelSources.loading]);

  // ── Screenshot source: paste or upload ─────────────────────────────────
  // EVERY image handed over, not just one. Pasting or dropping a batch of
  // screenshots used to keep the last file and silently discard the rest;
  // they are all uploaded here, in order, and all of them travel to the
  // issue as its image history (the last one is the default picture).
  const uploadScreenshot = useCallback(async (files) => {
    const list = (Array.isArray(files) ? files : [files]).filter(Boolean);
    if (!list.length) return;
    setUploadingScreenshot(true);
    const uploaded = [];
    try {
      for (const file of list) {
        const fd = new FormData();
        fd.append("screenshot", file, file.name || "pasted.png");
        const res = await fetch(`${url}/issue/proposals/screenshot`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem(tokenRef)}`,
          },
          body: fd,
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok || !body.url) throw new Error(body.err || "Upload failed");
        uploaded.push({ url: body.url, at: new Date().toISOString() });
      }
      addNotification(
        uploaded.length === 1
          ? "Screenshot attached (it is now the default image)"
          : `${uploaded.length} images attached - the last one is the default, the rest are kept as image history`,
        "success"
      );
    } catch (e) {
      addNotification(e.message || "Failed to upload screenshot", "danger");
    } finally {
      if (uploaded.length) {
        setScreenshotHistory((h) => [...h, ...uploaded]);
        setScreenshotUrl(uploaded[uploaded.length - 1].url);
      }
      setUploadingScreenshot(false);
    }
  }, []);

  // Clipboard paste anywhere on the page: an image becomes the issue image
  // (and focuses the Paste media tab); video / PDF / Excel files copied
  // from the file explorer queue as attachments for the new issue.
  const takeMediaFiles = useCallback((files) => {
    const arr = [...(files || [])].filter(Boolean);
    if (!arr.length) return;
    const images = arr.filter((f) => mediaKindOf(f) === "image");
    const others = arr.filter((f) => mediaKindOf(f) && mediaKindOf(f) !== "image");
    const bad = arr.filter((f) => !mediaKindOf(f));
    if (bad.length) addNotification(`${bad.length} file(s) skipped - only images, video, PDF and Excel/CSV are accepted`, "warning");
    if (images.length) uploadScreenshot(images);
    if (others.length) setPendingMedia((q) => [...q, ...others]);
    if (images.length || others.length) { setActiveTab("media"); setSourceType((t) => (t === "none" || t === "screenshot" ? "screenshot" : t)); }
  }, [uploadScreenshot]);
  useEffect(() => {
    const onPaste = (e) => {
      const tag = document.activeElement?.tagName;
      if (["INPUT", "TEXTAREA"].includes(tag) && !(e.clipboardData?.files || []).length) return; // typing in a field
      const dt = e.clipboardData;
      const files = dt?.files && dt.files.length ? [...dt.files]
        : [...(dt?.items || [])].filter((it) => it.kind === "file").map((it) => it.getAsFile()).filter(Boolean);
      if (!files.length) return;
      e.preventDefault();
      takeMediaFiles(files);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [takeMediaFiles]);

  // ── Viewpoints pushed from the Revit / Navisworks plugins ──────────────
  useEffect(() => {
    let isMounted = true;
    if (!selectedProject) {
      setViewpoints([]);
      setSelectedViewpoint(null);
      return undefined;
    }
    setLoadingViewpoints(true);
    fetch(`${url}/issue/viewpoints?projectId=${selectedProject}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem(tokenRef)}`,
      },
    })
      .then((r) => r.json())
      .then((body) => {
        if (isMounted) setViewpoints(Array.isArray(body?.viewpoints) ? body.viewpoints : []);
      })
      .catch((err) => {
        console.error("Failed to load pushed viewpoints:", err);
        if (isMounted) setViewpoints([]);
      })
      .finally(() => {
        if (isMounted) setLoadingViewpoints(false);
      });
    return () => { isMounted = false; };
  }, [selectedProject]);

  // ── Project preselected from the /issues toolbar (?projectId=…), else the
  //    project remembered from any coordination page / earlier login ─────
  const projectIdFromQuery = new URLSearchParams(location.search).get("projectId");
  const currentUserId = userInfoRedux?._id || userInfoRedux?.id;
  useEffect(() => {
    if (selectedProject || projects.length === 0) return;
    const wanted = projectIdFromQuery || getRememberedProject(currentUserId);
    if (wanted && projects.some((p) => p._id === wanted)) {
      handleProjectChange(wanted);
    } else if (!projectIdFromQuery) {
      // Nothing remembered (first visit): start on the first project like the
      // other coordination pages do - the user can still switch.
      handleProjectChange(projects[0]._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, projectIdFromQuery]);

  // Full project record (zipInfo → 3D model, isIfcProject) for a picker
  // entry; merged into `projects` so selectedProjectObj / the 3D loader
  // see it. Cached per id for the page's lifetime.
  const fullProjectCache = useRef(new Map());
  const loadFullProject = async (projectId) => {
    if (!projectId) return null;
    if (fullProjectCache.current.has(projectId)) return fullProjectCache.current.get(projectId);
    try {
      const r = await fetch(`${url}/projects/${projectId}`, { headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` } });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.err || "Failed to load the project");
      const full = body?.project || body?.data || body;
      if (full && full._id) {
        fullProjectCache.current.set(projectId, full);
        setProjects((prev) => prev.map((p) => (p._id === projectId ? { ...p, ...full, _light: false } : p)));
      }
      return full;
    } catch (e) {
      console.warn("full project load failed:", e?.message);
      return null;
    }
  };

  const handleProjectChange = async (projectId) => {
    console.log("═══════════════════════════════════");
    console.log("🎯 PROJECT SELECTED");
    console.log("═══════════════════════════════════");
    console.log("📝 Project ID:", projectId);
    console.log("📊 Available projects:", projects.length);

    const project = projects.find(p => p._id === projectId);
    console.log("✨ Found project:", project?.title);

    // Check if project has 3D model (zipInfo with GLB files)
    const has3DModel = project?.zipInfo?.paths?.some(path =>
      path.endsWith(".glb") || path.endsWith(".glb.gz")
    );
    console.log("🏗️ Project has 3D model?:", has3DModel);

    if (has3DModel && project?.zipInfo?.paths) {
      const glbFiles = project.zipInfo.paths.filter(path =>
        path.endsWith(".glb") || path.endsWith(".glb.gz")
      );
      console.log("📦 GLB files:", glbFiles);
    }

    // Clear previous 3D model
    console.log("🧹 Unloading previous 3D model");
    if (WebGL.current) {
      try {
        if (WebGL.current.loader && WebGL.current.loader.dispose) WebGL.current.loader.dispose();
        if (WebGL.current.viewer && WebGL.current.viewer.destroy) {
          WebGL.current.viewer.destroy();
        }
        if (WebGL.current.viewer && WebGL.current.viewer.renderer) {
          WebGL.current.viewer.renderer.dispose();
        }
      } catch (err) {
        console.warn("Cleanup error:", err);
      }
      WebGL.current = null;
    }

    // Clear viewer container
    if (viewerRef.current) {
      while (viewerRef.current.firstChild) {
        viewerRef.current.removeChild(viewerRef.current.firstChild);
      }
    }

    // Clear 3D states
    setModelLoaded(false);
    setLoading3D(false);
    loaded3DFor.current = null;

    // Clear previous sheets and thumbnails
    console.log("🧹 Clearing previous sheets and thumbnails");
    setDocControlSheets([]);
    setSheetThumbnails({});
    setSelectedSheets([]);
    setSelectedViewpoint(null);
    // Back to the default source option (Sheets tab) — source stays optional
    setSourceType("2d");
    setActiveTab("sheets");

    // Set the new project + remember it for every coordination page / next login
    setSelectedProject(projectId);
    rememberProject(currentUserId, projectId);
    if (!project || project._light || !project.zipInfo) loadFullProject(projectId);

    // Fetch sheets for the new project
    console.log("📡 Fetching sheets for new project:", projectId);
    fetchDocControlSheets(projectId);

    // Auto-set the title - and RE-set it when the project changes, as
    // long as it is still the one this page wrote. The old test was
    // "is the field empty", which is only true for the first project
    // picked: every switch after that kept the first project's name, so
    // an issue raised on project B was created titled "Issue - A".
    // A title the user typed themselves is never overwritten.
    {
      const current = form.getFieldValue("title");
      if (!current || current === autoTitleRef.current) {
        const next = `Issue - ${project?.title || 'New'}`;
        autoTitleRef.current = next;
        form.setFieldsValue({ title: next });
      }
    }

    console.log("✅ Project selection complete");
    console.log("═══════════════════════════════════");
  };

  const handleSheetClick = (sheet) => {
    // Click a picked sheet again to drop it; picking more than one keeps
    // them all. The first one left in the list is the active source.
    const picked = selectedSheets.some((s) => s._id === sheet._id);
    const next = picked
      ? selectedSheets.filter((s) => s._id !== sheet._id)
      : [...selectedSheets, sheet];
    setSelectedSheets(next);
    if (next.length) { setSourceType('2d'); setActiveTab('sheets'); }
  };
  const sheetIndex = (sheet) => selectedSheets.findIndex((s) => s._id === sheet._id);

  // Generate PDF thumbnail - exact copy from DocControl.jsx
  // Sheet thumbnail (first PDF page, or the image itself) - shared helper
  // handles gz sheets on R2 and plain images / PDFs mirrored from issues.
  const generateThumbnail = async (link, docId) => {
    try {
      const thumb = await renderPdfThumbnail(link);
      if (isMountedRef.current && thumb) setSheetThumbnails((prev) => ({ ...prev, [docId]: thumb }));
    } catch (error) {
      console.error('Error generating thumbnail:', error);
    }
  };

  // Thumbnails for the FIRST PAGE OF SHEETS, not all of them.
  //
  // The comment used to say "visible sheets" and the code did every one:
  // on the SpaceX project that is 128 PDFs downloaded, inflated and
  // rendered, and it fired twice because the sheet list is set by two
  // different fetches. Each one left an undestroyed pdf.js document and
  // a retained blob behind (both now fixed in helpers/pdfPreview), but
  // asking for 128 at all is the part that belongs here: nobody can see
  // 128 thumbnails, and the ones past the fold cost the same as the ones
  // on it.
  //
  // THUMB_BATCH is what the picker shows before scrolling. The rest are
  // rendered on demand by the sheet list itself when it draws them.
  const THUMB_BATCH = 12;
  useEffect(() => {
    if (!docControlSheets || !docControlSheets.length) return;
    const wanted = docControlSheets
      .filter((sheet) => sheet._id && sheet.link && !sheetThumbnails[sheet._id])
      .slice(0, THUMB_BATCH);
    if (!wanted.length) return;
    let cancelled = false;
    (async () => {
      // Sequential: the helper already gates at 4 concurrent, and there
      // is no reason to race a queue nobody is watching.
      for (const sheet of wanted) {
        if (cancelled || !isMountedRef.current) return;
        await generateThumbnail(sheet.link, sheet._id);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docControlSheets]);

  // Cleanup on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      setError(null);

      if (!selectedProject) {
        addNotification("Please select a project", "warning");
        setLoading(false);
        return;
      }

      // Source is OPTIONAL — an issue can start from nothing but a title
      // and description (sourceType 'blank'). What is attached decides:
      // a selected sheet -> 2d (or hybrid when the 3D model is the active
      // source too), 3D tab with a model -> 3d, a pushed viewpoint -> its
      // kind; the pasted image composes with any of them.
      // Map to backend enum values (valid enum: docControl, modelViewer,
      // blank, 2d, 3d, hybrid — never invent new values).
      const use3D = sourceType === '3d' && !!activeModel && !selectedViewpoint;
      const useSheet = !!selectedSheet && sourceType !== 'none';
      let backendSourceType;
      if (useSheet && use3D) {
        backendSourceType = 'hybrid';
      } else if (useSheet) {
        backendSourceType = '2d';
      } else if (use3D) {
        backendSourceType = '3d';
      } else if (sourceType === '3d' && selectedViewpoint?.kind === '3d') {
        backendSourceType = 'modelViewer';
      } else {
        // 'none', media-only, 2d-kind viewpoints, or nothing picked
        backendSourceType = 'blank';
      }

      console.log("📝 Creating issue with sourceType:", backendSourceType);

      const issueData = {
        title: values.title,
        // APPROVED TO RESOLVE at creation (owner 2026-10-26: the toggle is on
        // the create form). The SERVER decides whether to honour it - it is
        // ignored unless the creator is reviewer-side, because a checkbox on a
        // form is not a permission.
        ...(values.approvedToResolve ? { approvedToResolve: true } : {}),
        // The batch label. The server normalises it and composes the
        // stored title as "TAG-Title" (back/utils/issueTitleTag.js), so
        // nothing here has to build that string.
        titleTag: String((values.tags || [])[0] || ""),
        tags: (values.tags || []).map((t) => String(t).trim()).filter(Boolean),
        // The discipline / contractor picker: a contractor only when one was
        // picked under its discipline (validated against the project's
        // active partners server-side).
        ...(createPick && createPick.discipline ? { discipline: createPick.discipline } : {}),
        ...(createPick && createPick.responsibleCompanyId ? { responsibleCompanyId: createPick.responsibleCompanyId } : {}),
        description: values.description,
        projectId: selectedProject,
        estimatedHours: values.estimatedHours,
        // The server holds the web pages to the estimate rule; the plugins'
        // shipped builds send no marker and are let through.
        createdFrom: "web",
        sourceType: backendSourceType,
        priority: values.priority || "normal",
        publicPermission: values.publicPermission || "edit",
        // The picker holds addresses; the server normalises them and keeps
        // who added each one. An empty list is sent as an empty list rather
        // than omitted, so "I cleared it" and "I did not touch it" stay
        // different requests.
        recipients: selectedRecipients.map((e) => ({ email: String(e).trim().toLowerCase() })).filter((r) => r.email),
        responsibleUsers: selectedResponsibleUsers.filter((v) => !String(v).startsWith("email:")).length > 0
          ? selectedResponsibleUsers.filter((v) => !String(v).startsWith("email:"))
          : undefined,
        // No-account responsibles → backend mints guest contacts + magic links.
        responsibleEmails: selectedResponsibleUsers.filter((v) => String(v).startsWith("email:")).length > 0
          ? selectedResponsibleUsers.filter((v) => String(v).startsWith("email:")).map((v) => String(v).slice(6))
          : undefined,
      };

      // Location (Level / Area / Grid) - picked from the project's own
      // levels, areas and grid (LocationPicker), or typed; the server stores
      // a picked drawn area as areaId and flags typed values.
      const loc = values.location || {};
      // System and several levels travel too (2026-10-16) - the picker
      // offered both and the payload dropped them.
      if (loc.area || loc.level || loc.grid || loc.system || (loc.levels && loc.levels.length) || (loc.areas && loc.areas.length)
          || (loc.systems && loc.systems.length)) {
        issueData.location = {
          area: loc.area || "", level: loc.level || "", grid: loc.grid || "", system: loc.system || "",
          ...(Array.isArray(loc.levels) && loc.levels.length > 1 ? { levels: loc.levels } : {}),
          ...(Array.isArray(loc.areas) && loc.areas.length ? { areas: loc.areas } : {}),
          // Several systems (owner, 2026-11-04). Sent whenever the picker
          // gave a list: the server keeps the first as `system` and the list
          // as `systems`, so one system picked here stores as one system.
          ...(Array.isArray(loc.systems) && loc.systems.length ? { systems: loc.systems } : {}),
        };
      }

      // Due date — a bare "YYYY-MM-DD"; the server stores that day and
      // mirrors it onto plannedEnd for the Gantt issue lane.
      if (values.dueDate) {
        issueData.dueDate = values.dueDate;
      }

      // Linked plan item
      {
        const acts = (Array.isArray(values.ganttItemId) ? values.ganttItemId : [values.ganttItemId]).filter(Boolean);
        if (acts.length) {
          issueData.ganttItemId = acts[0];
          if (acts.length > 1) issueData.ganttItemIds = acts;
        }
      }
      // Evidence demanded before this can be marked resolved (enforced
      // server-side on every resolve path - partner, owner, plugin).
      {
        const ev = Array.isArray(values.evidencePolicy) ? values.evidencePolicy : ["screenshot"];
        issueData.evidencePolicy = {
          screenshot: ev.includes("screenshot"),
          document: ev.includes("document"),
          modelPush: ev.includes("modelPush"),
          pluginResolve: ev.includes("pluginResolve"),
        };
      }

      // Add 2D sheet data if selected. The FIRST pick is the active
      // markup surface (sourceDocument); every pick - that one included -
      // is listed in sourceDocuments so a multi-sheet selection arrives
      // on the issue whole instead of being cut down to its first entry.
      if (useSheet) {
        const sheetEntry = (sheet) => ({
          docControlId: sheet._id,
          url: sheet.link || sheet.documentPath,
          name: sheet.sheetName || sheet.sheetNumber,
          sheetNumber: sheet.sheetNumber,
          discipline: typeof sheet.discipline === "string"
            ? sheet.discipline
            : (sheet.discipline?.key || sheet.discipline?._id || ""),
        });
        issueData.sourceDocControlId = selectedSheet._id;
        issueData.sourceDocument = sheetEntry(selectedSheet);
        issueData.sourceDocuments = selectedSheets.map(sheetEntry);
      }

      // Add 3D model data if selected: the chosen export (project's latest,
      // a revision model version, or the elements exported against an issue).
      // A MODEL PUSH has no zipInfo, so it falls past this block on
      // purpose: POST /issue has no commit lane, and inventing a
      // sourceModel here would bypass the check that the push still has
      // geometry. It is attached right after creation instead.
      if (use3D && activeModel?.zipInfo) {
        issueData.sourceModelZipInfo = activeModel.zipInfo;
        issueData.sourceModel = {
          zipInfo: activeModel.zipInfo,
          name: activeModel.name,
          isIfcProject: !!activeModel.isIfcProject,
          manifestPath: activeModel.manifestPath || "",
          ...(activeModel.kind === "timeline" ? { kind: "timeline", timelineId: activeModel.timelineId, revisionId: activeModel.revisionId, exportedAt: activeModel.exportedAt } : {}),
          ...(activeModel.kind === "issue" ? { kind: "issue", fromIssueId: activeModel.fromIssueId, exportedAt: activeModel.exportedAt } : {}),
        };
      }

      // Pushed viewpoint (Revit / Navisworks) — carries its own
      // sourceModelViewer (viewportState / selectedElements / screenshot).
      if (sourceType === '3d' && selectedViewpoint?.sourceModelViewer) {
        issueData.sourceModelViewer = selectedViewpoint.sourceModelViewer;
      } else if (screenshotUrl) {
        // Pasted / uploaded screenshot — a valid source on its own, and it
        // composes with whichever 2D/3D choice was made.
        issueData.sourceModelViewer = { screenshot: screenshotUrl };
      }

      // Add canvas state
      issueData.canvasState = {
        width: 1200,
        height: 1600,
        scale: 1,
        backgroundColor: "#ffffff",
      };

      // Bolt-on 1 — freeze the "before" for auto-detect resolve.
      // sourceViewId lets the resolve UX re-focus on the same view.
      // openedAtSnapshotElements is best-effort: filled from the
      // viewer's currently-materialized instances if the 3D tab is
      // loaded, empty otherwise. Backend accepts empty gracefully.
      // See helpers/openedAtSnapshot.js for the collection logic.
      try {
        const snap = captureOpenedAtSnapshot(WebGL);
        if (snap?.sourceViewId) issueData.sourceViewId = snap.sourceViewId;
        if (Array.isArray(snap?.openedAtSnapshotElements)) {
          issueData.openedAtSnapshotElements = snap.openedAtSnapshotElements;
        }
      } catch (e) {
        console.warn("openedAt snapshot skipped:", e?.message);
      }

      console.log("Creating issue with data:", issueData);
      // With several pasted images: create with the OLDEST as the seed image,
      // then add the rest as versions in order - the newest ends up as the
      // default and the earlier ones sit in the image history with times.
      //
      // The picked default is posted LAST so it ends up as the current
      // image: clicking an earlier thumbnail used to set the default in
      // the UI and then get overwritten by whatever happened to be
      // newest, because the history was replayed in capture order
      // regardless of the choice.
      const timeline = screenshotHistory.length > 1 && issueData.sourceModelViewer?.screenshot === screenshotUrl
        ? [...screenshotHistory.filter((h) => h.url !== screenshotUrl),
           ...screenshotHistory.filter((h) => h.url === screenshotUrl)]
        : [];
      if (timeline.length) issueData.sourceModelViewer = { ...(issueData.sourceModelViewer || {}), screenshot: timeline[0].url };
      // The pair rides with the issue. The server writes both records, and if
      // the drawing turns out to be delivered it refuses the PAIR without
      // undoing the issue - the reason comes back as pairNotes.
      if (pairedDrawingId) issueData.pairedShopDrawingId = pairedDrawingId;
      const response = await createIssue(issueData);
      const newId = response?.data?._id;
      // A push is attached by id, through the endpoint that resolves its
      // versioned manifest and brings its diff along - the same call the
      // revision rail makes. A failure here is not a failed issue: it is
      // created, just without the 3D source, and the resolve page's 3D
      // MODEL select can set it.
      if (use3D && activeModel?.kind === "commit" && newId) {
        try {
          await setIssueSourceModel(newId, { commitId: activeModel.commitId });
        } catch (e) {
          addNotification(`Issue created, but the model push could not be attached: ${e.message || "failed"}`, "warning");
        }
      }
      if (timeline.length && newId) {
        for (const shot of timeline.slice(1)) {
          try { await saveIssueMarkup(newId, { imageUrl: shot.url, capturedAt: shot.at, note: "pasted screenshot" }); } catch (e) { /* history is best-effort */ }
        }
      }
      // Documents attached on the Sheets tab.
      if (attachDocs.length && newId) {
        // A few at a time: the server takes 20 per call and holds each
        // batch in memory.
        let failed = 0;
        for (let i = 0; i < attachDocs.length; i += 5) {
          const batch = attachDocs.slice(i, i + 5);
          try { await uploadIssueAttachments(newId, batch, { keepPicture: true }); } catch (e) { failed += batch.length; }
        }
        if (failed) addNotification(`Issue created, but ${failed} of ${attachDocs.length} document(s) could not be attached - add them from the issue page`, "warning");
      }
      // Media pasted / dropped in the Paste media tab (video, PDF, Excel/CSV)
      if (pendingMedia.length && newId) {
        try {
          await uploadIssueAttachments(newId, pendingMedia);
        } catch (e) {
          addNotification(`Issue created, but ${pendingMedia.length} attachment(s) could not be uploaded: ${e.message || "upload failed"}`, "warning");
        }
      }

      // A sheet AND a pasted picture: the resolve page opens on the
      // picture, fitted, with the sheet one click away in the media
      // history (helpers/issueSurface has the rule; this pins it for the
      // creator's browser before the redirect so nothing flashes).
      if (newId && selectedSheet && screenshotUrl) rememberSurface(newId, "image");

      addNotification("Issue created successfully!", "success");

      // EXTRA SCOPE (owner, 2026-10-21: an issue raised in a level, area or
      // system the owner/GC has signed off "should require extra scope and
      // should trigger a shop drawing"). The issue is already created and
      // marked - he chose create-and-mark over blocking - so this is an offer.
      if (response?.suggestShopDrawing && newId) {
        askForShopDrawing({
          message: response.extraScopeMessage || "This scope was signed off by the owner/GC.",
          // The shop drawing register, with this issue pre-selected to pair
          // with - the drawing is raised there, where its activity, its hours
          // and the responsible firm are asked for.
          onRaise: () => history.push(`/shop-drawings/${selectedProject}?pairIssue=${newId}`),
          onDecline: () => { declineExtraScopeDrawing(newId, { reason: "Declined on creation" }).catch(() => {}); },
        });
      }

      eventTrack(
        "Create-Issue-Success",
        "Create-Issue",
        userInfoRedux?.name,
        "+1",
        false,
        "+1"
      );

      // Navigate to the new issue viewer
      history.push(`/issue/${response.shareToken}/resolve`);
    } catch (err) {
      console.error("Failed to create issue:", err);
      setError(err.message || "Failed to create issue");
      addNotification(err.message || "Failed to create issue", "danger");
    } finally {
      setLoading(false);
    }
  };

  const selectedProjectObj = projects.find(p => p._id === selectedProject);
  const GLB_RE = /\.glb(\.gz)?$/i;
  const MANIFEST_RE = /(^|\/)manifest\.json(\.gz)?$/i;
  // An export mid-flight already has .glb paths in its zipInfo, so the
  // regex alone cannot tell a finished model from a partial one. Fail
  // open on an unknown status - see helpers/modelVisibility.
  const projectHasGlb = !isModelUploading(selectedProjectObj)
    && !!selectedProjectObj?.zipInfo?.paths?.some((path) => GLB_RE.test(String(path)));
  // With no project-level export, default to the newest model version /
  // issue export instead of an empty pane.
  // Newest push first: on a project with no project-level export it is
  // the most current picture of the model there is - a model version can
  // be months older.
  const effectiveSourceKey = modelSourceKey === "project" && !projectHasGlb && selectedProjectObj && !selectedProjectObj._light
    ? (modelSources.commits[0] ? `commit:${modelSources.commits[0].commitId}`
      : (modelSources.timelines[0] ? `timeline:${modelSources.timelines[0]._id}`
        : (modelSources.issues[0] ? `issue:${modelSources.issues[0]._id}` : "project")))
    : modelSourceKey;
  // The 3D export the viewer shows and the issue will be created with.
  const activeModel = (() => {
    if (!selectedProject) return null;
    if (effectiveSourceKey.startsWith("commit:")) {
      const c = modelSources.commits.find((x) => String(x.commitId) === effectiveSourceKey.slice(7));
      // No zipInfo by design - a push streams from the versioned manifest
      // its export wrote, and the issue is attached to it by commitId
      // after creation.
      if (!c || !c.manifestPath) return null;
      return { kind: "commit", key: effectiveSourceKey, commitId: c.commitId, manifestPath: c.manifestPath,
        name: `${c.title}${c.kind === "selection" ? " (selection)" : ""}`, exportedAt: c.ts };
    }
    if (effectiveSourceKey.startsWith("timeline:")) {
      const t = modelSources.timelines.find((x) => String(x._id) === effectiveSourceKey.slice(9));
      if (!t || !t.zipInfo) return null;
      const rev = t.revision ? `R${t.revision.revisionNumber}${t.revision.key ? ` ${t.revision.key}` : ""}` : "";
      return { kind: "timeline", key: effectiveSourceKey, timelineId: t._id, revisionId: t.revision?._id, zipInfo: t.zipInfo, manifestPath: t.manifestPath || "",
        name: [rev, t.title].filter(Boolean).join(" - ") || "Model version", isIfcProject: !!t.isIfcProject, exportedAt: t.date };
    }
    if (effectiveSourceKey.startsWith("issue:")) {
      const i = modelSources.issues.find((x) => String(x._id) === effectiveSourceKey.slice(6));
      if (!i || !i.zipInfo) return null;
      return { kind: "issue", key: effectiveSourceKey, fromIssueId: i._id, zipInfo: i.zipInfo, manifestPath: i.manifestPath || "",
        name: `${issueLabel(i)} - ${i.title}`, isIfcProject: !!i.isIfcProject, exportedAt: i.exportedAt };
    }
    if (!projectHasGlb || !selectedProjectObj?.zipInfo) return null;
    const manifestPath = (selectedProjectObj.zipInfo.paths || []).find((p) => MANIFEST_RE.test(String(p))) || "";
    return { kind: "project", key: `project:${selectedProject}`, zipInfo: selectedProjectObj.zipInfo, manifestPath,
      name: selectedProjectObj.title, isIfcProject: !!selectedProjectObj.isIfcProject };
  })();
  const anyModelSource = projectHasGlb || modelSources.commits.length > 0 || modelSources.timelines.length > 0 || modelSources.issues.length > 0;
  activeModelRef.current = activeModel;

  // Company users (when the project has no roster yet) and people added
  // through "Invite by email", under the roster's own groups.
  const rosterHas = (id) => participantRoster.some((c) => (c.users || []).some((cu) => String(cu.userId) === String(id)));
  const responsibleExtraGroups = [
    ...(participantRoster.length ? [] : [{
      label: "Company",
      key: "x:company",
      options: companyUsers.map((user) => ({ value: user._id, label: user.name || user.email, search: `${user.name || ""} ${user.email || ""}`.toLowerCase() })),
    }]),
    {
      label: "Invited",
      key: "x:invited",
      options: invitedUsers
        .filter((u) => !companyUsers.some((cu) => cu._id === u._id) && !rosterHas(u._id))
        .map((u) => ({
          value: u._id,
          label: u.contact ? `${u.email} (no account · email link)` : u.email,
          search: String(u.email || "").toLowerCase(),
        })),
    },
  ];

  return (
    <>
      {(loading || projectsLoading) && <Loader />}
      <div className="create-issue-page-container">
        <div style={{ padding: "12px 20px 0" }}>
          <CoordinationBreadcrumbs
            items={[{ label: "Issues Manager", to: "/issues" }]}
            current="New issue"
          />
        </div>
        {/* Header */}
        <div className="create-issue-header">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => history.push("/issues")}
          />
          <h2>Create New Issue</h2>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ margin: "0 20px 20px" }}
          />
        )}

        <div className="create-issue-content">
          {/* Left Panel - Form */}
          <div className="left-panel">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{
                // Shared links open editable by default. An issue is sent
                // to someone because they are the one who has to answer
                // it, and a link that only lets them read it sends them
                // back to ask for access.
                publicPermission: "edit",
              }}
            >
              {/* Project Selection — picked on the /issues toolbar when a
                  ?projectId= arrives; the Select only renders as a fallback */}
              <Form.Item
                label={projectIdFromQuery && selectedProjectObj ? "Project" : "Select Project"}
                required
              >
                {projectIdFromQuery && selectedProjectObj ? (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '6px 12px', background: '#FAFAFA',
                    border: '1px solid #EFEFEF', borderRadius: 6,
                    fontSize: 13, fontWeight: 500,
                  }}>
                    {selectedProjectObj.title}
                    <Tag style={{ margin: 0 }}>from Issues board</Tag>
                  </div>
                ) : (
                <Select
                  placeholder="Choose a project"
                  size="large"
                  loading={projectsLoading}
                  showSearch
                  optionFilterProp="children"
                  onChange={handleProjectChange}
                  value={selectedProject}
                  filterOption={(input, option) => {
                    // Get the project title from the option's children
                    // Children can be: string | [string, span] | [string, span, span]
                    const children = option.children;
                    const title = Array.isArray(children) ? children[0] : children;
                    return title && typeof title === 'string' &&
                           title.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                  }}
                >
                  {projects.map((project) => {
                    const has3D = project.zipInfo?.paths?.some(path =>
                      path.endsWith(".glb") || path.endsWith(".glb.gz")
                    );
                    const has2D = projectDocCounts[project._id] > 0;
                    return (
                      <Option key={project._id} value={project._id}>
                        {project.title}
                        {has2D && <span style={{ color: '#3A5765', marginLeft: 8 }}>📄 2D</span>}
                        {has3D && <span style={{ color: '#317A1B', marginLeft: 8 }}>🎨 3D</span>}
                      </Option>
                    );
                  })}
                </Select>
                )}
              </Form.Item>

              {/* Tag — a label shared by everything raised in one pass, so
                  the batch can be filtered and printed together later. It
                  is offered as a picker over the tags already in use on the
                  project, with free typing for a new one: a tag that is
                  spelled two ways is two filters and therefore no filter. */}
              {/* SEVERAL TAGS since 2026-10-15 (owner: "issues, can be
                  tagged by multiple tags"). The first one goes on the title;
                  the issue is found by any of them. */}
              <Form.Item
                name="tags"
                label="Tags (optional)"
                tooltip="Short labels, e.g. SNAG or RFI-7. The first is put in front of the title (SNAG-Duct clash); the issue shows up when the list is filtered by any of them."
              >
                <Select
                  mode="tags"
                  size="large"
                  allowClear
                  aria-label="Tags"
                  tokenSeparators={[","]}
                  options={tagOptions}
                  placeholder="e.g. SNAG, RFI-7, VISIT 12"
                  onChange={(v) => setTagPreview(String((v || [])[0] || "").toUpperCase())}
                  notFoundContent={selectedProject
                    ? "No tags on this project yet — type to start one"
                    : "Pick a project to see its tags"}
                />
              </Form.Item>

              {/* DISCIPLINE, WITH EACH CONTRACTOR UNDER IT (owner,
                  2026-10-15). Picking a contractor sets the issue's
                  responsible company too; a bare discipline sets only the
                  discipline. */}
              <Form.Item label="Discipline / contractor (optional)">
                <DisciplineContractorPicker
                  size="large"
                  disciplines={createDisciplines}
                  roster={participantRoster}
                  noRosterReason={selectedProject ? "" : "Pick a project to list its contractors"}
                  value={createPick}
                  onChange={(pick) => setCreatePick(pick)}
                />
              </Form.Item>

              {/* Title */}
              <Form.Item
                name="title"
                label="Issue Title"
                rules={[
                  { required: true, message: "Please enter a title" },
                  { min: 2, message: "Title must be at least 2 characters" },
                  { max: 100, message: "Title is too long" },
                ]}
                extra={tagPreview
                  ? `Will be saved as "${tagPreview}-<your title>"`
                  : undefined}
              >
                <Input placeholder="Enter issue title" size="large" />
              </Form.Item>

              {/* Description */}
              <Form.Item
                name="description"
                label="Description (optional)"
                rules={[{ max: 500, message: "Description is too long" }]}
              >
                <TextArea
                  placeholder="Add a description..."
                  rows={3}
                />
              </Form.Item>

              {/* Responsible Users — grouped by participant company */}
              <Form.Item
                label="Assign Responsible Users (optional)"
                tooltip="Select one or more users from any participant company who will be responsible for resolving this issue"
              >
                {/* The shared picker (components/PeoplePicker, owner
                    2026-10-15): every partner under its trade, supervisors
                    and users tagged. Values unchanged - a user id, or
                    `email:<addr>` for a declared contact with no account,
                    which the create route provisions as it always has. A
                    partner executive is listed but greyed out: a reader,
                    never responsible. */}
                <PeoplePicker
                  mode="multiple"
                  placeholder="Select users..."
                  size="large"
                  loading={loadingRoster || loadingUsers}
                  value={selectedResponsibleUsers}
                  onChange={setSelectedResponsibleUsers}
                  maxTagCount="responsive"
                  roster={participantRoster}
                  personValue={responsiblePersonValue}
                  executives="disable"
                  extraGroups={responsibleExtraGroups}
                />
                <Button
                  size="small"
                  type="link"
                  icon={<MailOutlined />}
                  style={{ paddingLeft: 0, marginTop: 4 }}
                  disabled={!selectedProject}
                  onClick={() => setInviteModalOpen(true)}
                >
                  Invite by email
                </Button>
                {selectedResponsibleUsers.length > 0 && (
                  <div style={{ marginTop: 8, color: '#317A1B', fontSize: 12 }}>
                    ✓ {selectedResponsibleUsers.length} user{selectedResponsibleUsers.length > 1 ? 's' : ''} selected
                  </div>
                )}
              </Form.Item>

              {/* WHO RECEIVES THE DELIVERABLE, and only that. Owner,
                  2026-09-30: "make sure the each issue has an optional
                  recepient, who will get the delvierables, the moment they
                  get approved". Distinct from the responsible users above,
                  who hear about every change - a recipient is told ONCE,
                  when the work is approved, and gets the file.
                  OPTIONAL MEANS SILENT: leaving it empty is the ordinary
                  case and sends nobody anything. */}
              <Form.Item
                label="Deliverable recipients (optional)"
                tooltip="Mailed once, when this issue is approved, with the deliverable attached - or a download link if it is too large. They are not told about changes; that is the responsible users."
              >
                <PeoplePicker
                  mode="tags"
                  placeholder="Search the project's partners, or type any address"
                  size="large"
                  loading={loadingRoster}
                  value={selectedRecipients}
                  onChange={setSelectedRecipients}
                  tokenSeparators={[",", " "]}
                  maxTagCount="responsive"
                  roster={participantRoster}
                  personValue={recipientPersonValue}
                />
              </Form.Item>

              {/* Due date — the day the work on this issue is expected to
                  be done. Day precision, sent as the bare "YYYY-MM-DD" the
                  API stores; it also seeds the issue's planned end on the
                  Gantt issue lane, and drives the list's "Due today"
                  filter, the overdue highlight and the reminder emails. */}
              <Form.Item
                name="dueDate"
                label="Due date (optional)"
                initialValue=""
                tooltip="When this issue has to be resolved. Overdue issues are flagged on the list and the board, and are the ones 'Remind all overdue' emails about."
              >
                <Input type="date" size="large" style={{ maxWidth: 220 }} />
              </Form.Item>

              {/* Priority — the field the list, the board and every
                  digest sort by. It was only settable AFTER the issue
                  existed (row action / resolve page), so everything was
                  raised "normal" and had to be re-triaged by hand. */}
              <Form.Item
                name="priority"
                label="Priority"
                initialValue="normal"
                tooltip="Urgent issues are pulled to the top of the list and the board, and are called out in the reminder digests."
              >
                <Select size="large" style={{ maxWidth: 220 }}>
                  <Option value="urgent">🔴 Urgent</Option>
                  <Option value="normal">🟡 Normal</Option>
                  <Option value="low">🟢 Low</Option>
                </Select>
              </Form.Item>

              {/* APPROVED TO RESOLVE (owner, 2026-10-26). Advisory - it blocks
                  nothing - and the SERVER ignores it unless the creator is
                  reviewer-side, so offering it here cannot grant anything. A
                  contractor ticking it simply raises an unapproved issue, which
                  is what would have happened anyway. */}
              <Form.Item
                name="approvedToResolve"
                valuePropName="checked"
                tooltip="A green circle beside the id tells the assignees this issue is cleared to work on. They are emailed once, together with anything else approved at the same time. Only a reviewer can set it - if you are not one, this is ignored."
              >
                <Checkbox data-testid="create-approved-to-resolve">Approved to resolve</Checkbox>
              </Form.Item>

              {/* ESTIMATED HOURS (owner, 2026-10-16) - required; the contractor
                  cost page projects cost from it. */}
              <Form.Item
                name="estimatedHours"
                label="Estimated hours"
                rules={[{ required: true, message: "Enter the estimated hours" }]}
                tooltip="How long this issue should take. The contractor cost page projects cost from it."
              >
                <InputNumber size="large" aria-label="Estimated hours" min={0.25} step={0.25} precision={2} addonAfter="hours" placeholder="e.g. 2.5" style={{ maxWidth: 220 }} />
              </Form.Item>

              {/* Location: Level / Area / Grid - the project's own lists, searchable, or typed */}
              {/* THE PICKER IS THE FIELD'S ONLY CHILD (2026-10-16: "when selecting a
                  level, it dosen't pass"). It shared this Form.Item with the
                  sheet-pairing block below, and antd hands value/onChange only
                  to a single child - so nothing picked here was ever kept. */}
              <Form.Item label="Location (optional)" name="location">
                <LocationPicker projectId={selectedProject} multiLevel multiArea multiSystem />
              </Form.Item>

                {/* ONE PIECE OF WORK, ONE SET OF HOURS (owner, 2026-10-08).
                    Offered on the SHEETS source, because that is what "sheet
                    delivery" means here: the issue is about a drawing, so
                    which drawing it belongs with is a real question. Hidden
                    rather than disabled elsewhere - an empty picker on a 3D
                    issue is a question nobody asked. */}
                {sourceType === "2d" && pairable.length > 0 && (
                  <Form.Item
                    label="Pair with a shop drawing request"
                    extra="The pair counts ONE set of hours: enter them on either side and the other will say they are already accounted for. Only undelivered requests can be paired."
                    style={{ marginTop: 8 }}
                  >
                    <Select filterOption={matchOption}
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder="Not paired"
                      value={pairedDrawingId || undefined}
                      onChange={(v) => setPairedDrawingId(v || null)}
                      options={pairable.map((d) => ({
                        value: d._id,
                        label: [d.sheetNumber, d.name].filter(Boolean).join(" - ")
                          + (d.hours ? `  (${d.hours} h already on the drawing)` : ""),
                      }))}
                    />
                  </Form.Item>
                )}

              {/* Plan item link */}
              <Form.Item
                name="ganttItemId"
                label={ganttItems.length ? "Schedule activity" : "Schedule activity (optional)"}
                tooltip="Link this issue to an activity on the project schedule - required once the project has a schedule"
                rules={[{ required: ganttItems.length > 0, message: "Pick the schedule activity this issue belongs to" }]}
              >
                {/* THE LOWEST ACTIVITY ONLY, its path beneath (owner,
                    2026-10-15) - helpers/schedule/leafActivityOptions. A
                    disabled (called-off) activity is refused by the server,
                    so it is not offered. */}
                {/* SEVERAL, ALL EQUAL (owner, 2026-10-16: "some of the issues
                    are very generic, for level1 and level2, i can't map them
                    to 1 activity") - e.g. Level 1 › Overall and Level 2 ›
                    Overall. The issue counts in full on each. */}
                <Select filterOption={matchOption}
                  mode="multiple"
                  allowClear
                  aria-label="Schedule activities"
                  placeholder="Link to one or more schedule activities..."
                  {...LEAF_SELECT_PROPS}
                  disabled={!selectedProject || ganttItems.length === 0}
                  options={leafActivityOptions(ganttItems, { offer: (it) => !it.disabled })}
                />
              </Form.Item>

              {/* Evidence policy: what the responsible party must provide
                  before the issue can be marked resolved. The server refuses
                  resolves / proposals that miss any of these. */}
              <Form.Item
                name="evidencePolicy"
                label="Require proof to mark resolved"
                tooltip="Partners, supervisors and owners cannot submit a resolution until they provide every item ticked here - the update is blocked on save."
                initialValue={["screenshot"]}
              >
                <Checkbox.Group
                  options={[
                    { label: "Screenshot of the progress", value: "screenshot" },
                    { label: "Uploaded document (PDF / Excel / CSV sheet)", value: "document" },
                    { label: "Model push / diff from Revit or Navisworks (linked commit)", value: "modelPush" },
                    { label: "Resolution pushed from the Revit / Navisworks plugin", value: "pluginResolve" },
                  ]}
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                />
              </Form.Item>

              <Modal
                title="Invite a responsible person by email"
                open={inviteModalOpen}
                onCancel={() => setInviteModalOpen(false)}
                onOk={submitInvite}
                okText="Send invite"
                confirmLoading={inviting}
                destroyOnClose
              >
                <p style={{ fontSize: 13, color: "#8D8D8D" }}>
                  They'll get an email invite to register as a responsible
                  person on this project. If the email already has an
                  account, it's selected here directly.
                </p>
                <Input
                  type="email"
                  placeholder="name@company.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onPressEnter={submitInvite}
                />
              </Modal>

              {/* Source (optional, single-select): the three buttons mirror the
                  three tabs of the source panel on the right - clicking one
                  puts that tab in focus; clicking the active one again
                  deselects it (comment-only issue). */}
              <Form.Item
                label="Source (optional)"
                help="Optional - pick one: a 2D sheet, media from your clipboard, or the 3D model. Click again to deselect (comment-only issue)."
              >
                <div className="source-type-buttons" style={{ flexWrap: 'wrap' }}>
                  <Button
                    type={sourceType === '2d' ? 'primary' : 'default'}
                    icon={<FileImageOutlined />}
                    onClick={() => pickSource('2d')}
                    size="large"
                    disabled={!selectedProject}
                    aria-pressed={sourceType === '2d'}
                  >
                    Sheets{selectedSheet ? ` - ${selectedSheet.sheetNumber || selectedSheet.sheetName}${selectedSheets.length > 1 ? ` +${selectedSheets.length - 1}` : ""}` : ""}
                  </Button>
                  <Button
                    type={sourceType === 'screenshot' ? 'primary' : 'default'}
                    icon={<SnippetsOutlined />}
                    onClick={() => pickSource('screenshot')}
                    size="large"
                    aria-pressed={sourceType === 'screenshot'}
                  >
                    Paste media from clipboard{screenshotUrl || pendingMedia.length ? ` (${(screenshotUrl ? 1 : 0) + pendingMedia.length})` : ""}
                  </Button>
                  <Button
                    type={sourceType === '3d' ? 'primary' : 'default'}
                    icon={<AppstoreOutlined />}
                    onClick={() => pickSource('3d')}
                    size="large"
                    disabled={!selectedProject}
                    aria-pressed={sourceType === '3d'}
                  >
                    3D model{selectedProjectObj && !selectedProjectObj._light && !modelSources.loading && !anyModelSource ? " (none on this project)" : ""}
                  </Button>
                </div>

                {selectedSheet && sourceType === '3d' && (
                  <div style={{ marginTop: 4, fontSize: 12, color: '#3A5765' }}>
                    {selectedSheets.length > 1
                      ? <>All <b>{selectedSheets.length}</b> picked sheets stay attached - the issue is created as 2D + 3D.</>
                      : <>Sheet <b>{selectedSheet.sheetNumber || selectedSheet.sheetName}</b> stays attached - the issue is created as 2D + 3D.</>}
                    <Button type="link" size="small" onClick={() => setSelectedSheets([])} style={{ padding: '0 4px' }}>{selectedSheets.length > 1 ? "Drop sheets" : "Drop sheet"}</Button>
                  </div>
                )}

                <div style={{ marginTop: 8, fontSize: 12, color: '#8D8D8D' }}>
                  Element selections and viewpoints stream in automatically from
                  the Revit / Navisworks plugins when they're connected - pushed
                  viewpoints are listed under the 3D tab.
                </div>

                {selectedProject && (
                  <div className="source-availability-info">
                    {loadingSheets ? (
                      <p className="loading">Loading 2D sheets...</p>
                    ) : docControlSheets.length > 0 ? (
                      <p className="available">✓ {docControlSheets.length} 2D sheets available</p>
                    ) : (
                      <p className="unavailable">✗ No 2D sheets found</p>
                    )}
                    {selectedProjectObj?._light || modelSources.loading ? (
                      <p className="loading">Checking for 3D models...</p>
                    ) : anyModelSource ? (
                      <p className="available">✓ 3D: {[projectHasGlb ? "latest export" : "", modelSources.commits.length ? `${modelSources.commits.length} push${modelSources.commits.length > 1 ? "es" : ""}` : "", modelSources.timelines.length ? `${modelSources.timelines.length} model version${modelSources.timelines.length > 1 ? "s" : ""}` : "", modelSources.issues.length ? `${modelSources.issues.length} issue export${modelSources.issues.length > 1 ? "s" : ""}` : ""].filter(Boolean).join(" · ")}</p>
                    ) : (
                      <p className="unavailable">✗ No 3D model found</p>
                    )}
                  </div>
                )}
              </Form.Item>

              {/* Public Permission */}
              <Form.Item
                name="publicPermission"
                label="Default Permission for Shared Links"
              >
                <Radio.Group>
                  <Radio.Button value="view">View Only</Radio.Button>
                  <Radio.Button value="comment">Can Comment</Radio.Button>
                  <Radio.Button value="edit">Can Edit</Radio.Button>
                </Radio.Group>
              </Form.Item>

              {/* Submit */}
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={loading}
                  disabled={!selectedProject}
                >
                  Create Issue
                </Button>
              </Form.Item>
            </Form>
          </div>

          {/* Source panel - ONE panel with three tabs (Sheets is the default),
              mirrored by the three source buttons on the left. The 3D viewer
              container stays mounted while another tab is active (the WebGL
              app is created once per project). */}
          <div className="source-panel">
            <Tabs
              activeKey={activeTab}
              onChange={(k) => {
                setActiveTab(k);
                // The tab in focus is the selected source (single-select).
                setSourceType(SOURCE_FOR[k] || "none");
                if (k === "3d") setTimeout(() => { try { window.dispatchEvent(new Event("resize")); } catch (e) { /* ignore */ } }, 50);
              }}
              className="source-tabs"
              items={[
                {
                  key: "sheets",
                  label: <span><FileImageOutlined /> Sheets{selectedSheets.length ? <span style={{ color: '#317A1B', marginLeft: 6 }}><CheckOutlined />{selectedSheets.length > 1 ? ` ${selectedSheets.length}` : ""}</span> : null}</span>,
                  children: (
                    <div className="right-panel source-pane">

            <div className="sheets-header">
              <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <FileImageOutlined style={{ marginRight: 8 }} />
                <span>2D Sheets</span>
                {selectedSheets.length > 0 && (
                  <span style={{ color: '#317A1B', marginLeft: 8, fontSize: 12 }}>
                    <CheckOutlined /> {selectedSheets.length} selected
                    {selectedSheets.length > 1 ? " - the first one opens for markup" : ""}
                  </span>
                )}
              </div>
              <Upload
                multiple
                accept={MEDIA_ACCEPT}
                showUploadList={false}
                beforeUpload={(file, list) => {
                  // Called once per file; take the whole batch on its first file.
                  if (file !== list[0]) return false;
                  const ok = list.filter((f) => mediaKindOf(f) && (f.size || 0) <= 100 * 1024 * 1024);
                  const bad = list.filter((f) => !mediaKindOf(f)).length;
                  const big = list.filter((f) => mediaKindOf(f) && (f.size || 0) > 100 * 1024 * 1024).length;
                  if (bad) addNotification(`${bad} file(s) skipped - only ${MEDIA_ALLOWED_TEXT} can be attached`, "warning");
                  if (big) addNotification(`${big} file(s) skipped - over 100 MB`, "warning");
                  if (ok.length) setAttachDocs((q) => [...q, ...ok]);
                  return false;
                }}
              >
                <Button size="small" icon={<PaperClipOutlined />} disabled={!selectedProject} style={{ marginRight: 8 }} aria-label="Attach documents">
                  Attach documents{attachDocs.length ? ` (${attachDocs.length})` : ""}
                </Button>
              </Upload>
              {docControlSheets.length > 0 && (
                <Input
                  placeholder="Search sheets..."
                  prefix={<SearchOutlined />}
                  value={sheetSearchQuery}
                  onChange={(e) => setSheetSearchQuery(e.target.value)}
                  style={{ width: 200 }}
                  allowClear
                />
              )}
            </div>

            {attachDocs.length > 0 && (
              <div data-testid="attach-docs" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '6px 0 8px' }}>
                <span style={{ fontSize: 11, color: '#8D8D8D', alignSelf: 'center' }}>Attached when the issue is created:</span>
                {attachDocs.map((f, i) => (
                  <Tag key={`${f.name}-${i}`} closable onClose={(e) => { e.preventDefault(); setAttachDocs((q) => q.filter((_, j) => j !== i)); }}
                       icon={<PaperClipOutlined />} style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }} title={f.name}>
                    {f.name}
                  </Tag>
                ))}
              </div>
            )}
            {!selectedProject ? (
              <Empty
                description="Select a project to view 2D sheets"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ marginTop: 40 }}
              />
            ) : loadingSheets ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Spin size="large" />
                <p style={{ marginTop: 16, color: '#8D8D8D' }}>Loading sheets...</p>
              </div>
            ) : docControlSheets.length === 0 ? (
              <Empty
                description="No 2D sheets found in this project"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ marginTop: 40 }}
              />
            ) : (
              <div className="sheets-list">
                {docControlSheets
                  .filter((sheet) => {
                    if (!sheetSearchQuery) return true;
                    const query = sheetSearchQuery.toLowerCase();
                    return (
                      sheet.sheetNumber?.toLowerCase().includes(query) ||
                      sheet.sheetName?.toLowerCase().includes(query) ||
                      (typeof sheet.discipline === 'string' && sheet.discipline.toLowerCase().includes(query)) ||
                      (typeof sheet.discipline === 'object' && (
                        sheet.discipline?.key?.toLowerCase().includes(query) ||
                        sheet.discipline?.value?.toLowerCase().includes(query)
                      ))
                    );
                  })
                  .map((sheet) => (
                  <Card
                    key={sheet._id}
                    hoverable
                    className={`sheet-card ${sheetIndex(sheet) >= 0 ? 'selected' : ''}`}
                    onClick={() => handleSheetClick(sheet)}
                    styles={{ body: { padding: 12 } }}
                  >
                    <div className="sheet-thumbnail">
                      {sheetThumbnails[sheet._id] ? (
                        <img
                          src={sheetThumbnails[sheet._id]}
                          alt={sheet.sheetName}
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                      ) : (sheet.link || sheet.documentPath) ? (
                        <span style={{ fontSize: '10px', color: '#AEAEAE' }}>Loading...</span>
                      ) : (
                        <FileImageOutlined style={{ fontSize: 48, color: '#E2E2E2' }} />
                      )}
                      {sheetIndex(sheet) >= 0 && (
                        <div className="selected-overlay">
                          <CheckOutlined style={{ fontSize: 24, color: 'white' }} />
                          {/* Pick order matters: #1 is the sheet the
                              issue opens on, the rest are attached. */}
                          <div style={{ color: 'white', fontSize: 11, fontWeight: 600, marginTop: 2 }}>
                            {sheetIndex(sheet) === 0 ? 'Opens for markup' : `Attached #${sheetIndex(sheet) + 1}`}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="sheet-info">
                      <div className="sheet-number">{sheet.sheetNumber}</div>
                      <div className="sheet-name">{sheet.sheetName || 'Untitled'}</div>
                      {sheet.discipline && (
                        <div className="sheet-discipline">
                          {typeof sheet.discipline === 'string'
                            ? sheet.discipline
                            : (sheet.discipline.value || sheet.discipline.key || sheet.discipline._id)}
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}

                    </div>
                  ),
                },
                {
                  key: "media",
                  label: <span><SnippetsOutlined /> Paste media from clipboard{screenshotUrl || pendingMedia.length ? <CheckOutlined style={{ color: '#317A1B', marginLeft: 6 }} /> : null}</span>,
                  children: (
                    <div className="media-pane source-pane">
                      <div
                        tabIndex={0}
                        onDrop={(e) => { e.preventDefault(); takeMediaFiles(e.dataTransfer?.files); }}
                        onDragOver={(e) => e.preventDefault()}
                        style={{
                          border: '2px dashed #455D6B', borderRadius: 8, padding: '22px 16px', textAlign: 'center',
                          background: '#f7f9fc', outline: 'none', marginBottom: 14,
                        }}
                      >
                        <SnippetsOutlined style={{ fontSize: 28, color: '#455D6B' }} />
                        <div style={{ fontWeight: 600, color: '#23343E', marginTop: 6 }}>Press Ctrl+V (Cmd+V on Mac) anywhere on this page</div>
                        <div style={{ fontSize: 12, color: '#8D8D8D', marginTop: 4 }}>
                          An image becomes the issue image (every paste is kept in the image history). Video, PDF and Excel/CSV files copied from your file explorer are attached to the issue when it is created. You can also drop files here.
                        </div>
                        <div style={{ marginTop: 10, display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                          <Upload
                            accept={MEDIA_ACCEPT}
                            multiple
                            showUploadList={false}
                            disabled={uploadingScreenshot}
                            beforeUpload={(file, fileList) => {
                              // antd calls this once per file; handle the batch on the first call
                              if (fileList && fileList[0] === file) takeMediaFiles(fileList);
                              return false;
                            }}
                          >
                            <Button icon={<UploadOutlined />} loading={uploadingScreenshot}>
                              {screenshotUrl ? "Replace image / add files" : "...or choose files"}
                            </Button>
                          </Upload>
                        </div>
                      </div>
                      {screenshotUrl && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 11, color: '#8D8D8D', marginBottom: 4 }}>Issue image (default)</div>
                          <div style={{
                            display: 'inline-block',
                            border: '1px solid #EFEFEF', borderRadius: 6,
                            padding: 4, background: '#FAFAFA',
                          }}>
                            <img
                              src={previewSrc(screenshotUrl)}
                              alt="Attached screenshot"
                              style={{ display: 'block', maxHeight: 220, maxWidth: '100%', objectFit: 'contain', borderRadius: 4 }}
                            />
                          </div>
                        </div>
                      )}
                      {screenshotHistory.length > 1 && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ fontSize: 11, color: '#8D8D8D', marginBottom: 4 }}>
                            Image timeline ({screenshotHistory.length}) - newest is the default; earlier ones are kept as history on the issue. Click one to make it the default.
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {screenshotHistory.slice().reverse().map((h, i) => (
                              <div key={h.url + i} onClick={() => setScreenshotUrl(h.url)} title={new Date(h.at).toLocaleString()}
                                   style={{ border: `2px solid ${h.url === screenshotUrl ? '#1a1a2e' : '#EFEFEF'}`, borderRadius: 6, padding: 2, cursor: 'pointer', background: '#fff' }}>
                                <img src={previewSrc(h.url)} alt="" style={{ display: 'block', width: 72, height: 50, objectFit: 'cover', borderRadius: 4 }} />
                                <div style={{ fontSize: 10, color: '#8D8D8D', textAlign: 'center' }}>{new Date(h.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {pendingMedia.length > 0 && (
                        <div>
                          <div style={{ fontSize: 11, color: '#8D8D8D', marginBottom: 4 }}>
                            Files to attach when the issue is created ({pendingMedia.length})
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {pendingMedia.map((f, i) => {
                              const k = mediaKindOf(f) || 'file';
                              const Icon = k === 'pdf' ? FilePdfOutlined : k === 'excel' ? FileExcelOutlined : k === 'video' ? VideoCameraOutlined : FileImageOutlined;
                              const color = k === 'pdf' ? '#d64545' : k === 'excel' ? '#1f8f4e' : k === 'video' ? '#7b3fe4' : '#2f6fed';
                              return (
                                <div key={`${f.name}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #EFEFEF', borderRadius: 6, padding: '6px 8px', background: '#fff' }}>
                                  <Icon style={{ fontSize: 20, color, width: 32, textAlign: 'center' }} />
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 12.5, fontWeight: 600, color: '#23343E', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</div>
                                    <div style={{ fontSize: 11, color: '#8D8D8D' }}>{k.toUpperCase()}{f.size ? ` · ${f.size < 1048576 ? `${Math.max(1, Math.round(f.size / 1024))} KB` : `${(f.size / 1048576).toFixed(1)} MB`}` : ''}</div>
                                  </div>
                                  <Button size="small" type="text" icon={<CloseOutlined />} onClick={() => setPendingMedia((q) => q.filter((_, j) => j !== i))} />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  key: "3d",
                  label: <span><AppstoreOutlined /> 3D model{modelLoaded ? <CheckOutlined style={{ color: '#317A1B', marginLeft: 6 }} /> : null}</span>,
                  children: (
                    <div className="center-panel source-pane">

            {!selectedProject ? (
              <Empty
                description="Select a project to view 3D model"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : anyModelSource ? (
              <div className="model-preview-container">
                <div className="model-preview-header" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <AppstoreOutlined style={{ marginRight: 8 }} />
                  <span>3D Model Preview</span>
                  {modelLoaded && (
                    <CheckOutlined style={{ color: '#317A1B', marginLeft: 8 }} />
                  )}
                  <span style={{ marginLeft: "auto", fontSize: 10.5, fontWeight: 600, color: "#7A8A94", letterSpacing: ".06em" }}>3D SOURCE</span>
                  <Select
                    size="small"
                    showSearch
                    value={effectiveSourceKey}
                    onChange={(v) => { setModelSourceKey(v); setModelLoaded(false); }}
                    style={{ minWidth: 260, maxWidth: 420 }}
                    dropdownMatchSelectWidth={false}
                    dropdownStyle={{ minWidth: 420 }}
                    optionLabelProp="title"
                    filterOption={(input, option) => String(option?.search || option?.title || "").toLowerCase().includes(input.toLowerCase())}
                    options={[
                      ...(projectHasGlb ? [{ label: <span style={{ fontSize: 10.5, letterSpacing: ".06em" }}>PROJECT</span>, options: [{ value: "project", title: `${selectedProjectObj?.title || "Project"} - latest export`, search: "project latest export", label: <span style={{ fontSize: 12.5 }}>{selectedProjectObj?.title || "Project"} <span style={{ color: "#7A8A94" }}>- latest export</span></span> }] }] : []),
                      ...(modelSources.commits.length ? [{ label: <span style={{ fontSize: 10.5, letterSpacing: ".06em" }}>MODEL PUSHES · SYNCS FROM REVIT / NAVISWORKS</span>, options: modelSources.commits.map((c) => {
                        const title = `${c.title}${c.kind === "selection" ? " (selection)" : ""}`;
                        const when = c.ts ? new Date(c.ts).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "";
                        const delta = [c.counts?.added ? `+${c.counts.added}` : "", c.counts?.modified ? `~${c.counts.modified}` : "", c.counts?.removed ? `\u2212${c.counts.removed}` : ""].filter(Boolean).join(" ");
                        return { value: `commit:${c.commitId}`, title, search: `${c.title} ${c.by || ""} ${c.narrative || ""} ${when} push sync revision`.toLowerCase(), label: (
                          <div style={{ lineHeight: 1.25 }}>
                            <div style={{ fontWeight: 600, fontSize: 12.5 }}>{title}{c.live ? <span style={{ marginLeft: 6, fontSize: 10, color: "#2E7D32" }}>LIVE</span> : null}</div>
                            <div style={{ fontSize: 11, color: "#7A8A94" }}>{[when, c.by, delta || (c.hasDiff ? "no change" : ""), "streams"].filter(Boolean).join(" · ")}</div>
                          </div>) };
                      }) }] : []),
                      ...(modelSources.timelines.length ? [{ label: <span style={{ fontSize: 10.5, letterSpacing: ".06em" }}>MODEL VERSIONS · PROJECT REVISIONS</span>, options: modelSources.timelines.map((t) => {
                        const rev = t.revision ? `R${t.revision.revisionNumber}${t.revision.key ? ` ${t.revision.key}` : ""}` : "";
                        const title = [rev, t.title].filter(Boolean).join(" · ") || "Model version";
                        const when = t.date ? new Date(t.date).toLocaleDateString() : "";
                        return { value: `timeline:${t._id}`, title, search: `${rev} ${t.title} ${when}`.toLowerCase(), label: (
                          <div style={{ lineHeight: 1.25 }}>
                            <div style={{ fontWeight: 600, fontSize: 12.5 }}>{title}</div>
                            <div style={{ fontSize: 11, color: "#7A8A94" }}>{[when, t.manifestPath ? "streams" : `${t.glbCount || t.fileCount} files`].filter(Boolean).join(" · ")}</div>
                          </div>) };
                      }) }] : []),
                      ...(modelSources.issues.length ? [{ label: <span style={{ fontSize: 10.5, letterSpacing: ".06em" }}>ELEMENTS EXPORTED AGAINST ISSUES · REVIT / NAVISWORKS</span>, options: modelSources.issues.map((i) => {
                        const title = `${issueLabel(i)} · ${i.title}`;
                        return { value: `issue:${i._id}`, title, search: `mk-${i.displayId} ${i.title} ${i.name || ""}`.toLowerCase(), label: (
                          <div style={{ lineHeight: 1.25 }}>
                            <div style={{ fontWeight: 600, fontSize: 12.5 }}>{title}</div>
                            <div style={{ fontSize: 11, color: "#7A8A94" }}>{[i.name, i.manifestPath ? "streams" : `${i.glbCount || i.fileCount} files`].filter(Boolean).join(" · ")}</div>
                          </div>) };
                      }) }] : []),
                    ]}
                  />
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
                  <ViewerViewButtons viewer={() => WebGL.current && WebGL.current.viewer} variant="floating" />
                  <ViewerEffectsButton viewer={() => WebGL.current && WebGL.current.viewer} variant="floating" style={{ width: 26, height: 26, borderRadius: 4 }} />
                </div>
                <div
                  ref={viewerRef}
                  id="WebglViewer"
                  className="model-preview-viewer"
                  style={{
                    width: '100%',
                    height: '500px',
                    background: '#1a1a1a',
                    position: 'relative',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}
                >
                  {/* Hidden element required by WebGL viewer for screenshot functionality */}
                  <a id="take-snapshot" style={{ display: 'none' }}></a>
                  {/* THE SHARED TOOLBAR, INSIDE the viewer box. It was
                      mounted in the header row ABOVE #WebglViewer, and it
                      positions itself absolutely - so it anchored to
                      whatever was positioned above the viewer instead of
                      to the viewer. A direct child of this
                      position:relative box is where the model view has
                      it. gear={false}: the header keeps its own
                      ViewerEffectsButton. */}
                  {/* A CORNER OF ITS OWN. `viewer-zoom` only carries coordinates in
                      the model-view and timeline stylesheets; on this page the class
                      is inert, so without a positioned wrapper the toolbar sits
                      wherever it falls in flow (Mark measured x=-41 on tracking,
                      lying across twenty controls). */}
                  <div style={{ position: "absolute", left: 12, right: 12, bottom: 12, zIndex: 5,
                   display: "flex", justifyContent: "flex-end", pointerEvents: "none" }}>
                    <ViewerToolbar viewer={() => WebGL.current && WebGL.current.viewer} gear={false} />
                  </div>
                  {loading3D && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 10
                    }}>
                      <Spin size="large" />
                      <p style={{ marginTop: 16, color: '#fff', textAlign: 'center' }}>
                        Loading 3D model...
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <Empty
                description="This project has no 3D model"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}

                      {/* Viewpoints pushed by the Revit / Navisworks plugins */}
                      {selectedProject && (
                        <div style={{ width: '100%', marginTop: 16 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#23343E', marginBottom: 6 }}>
                            <CameraOutlined style={{ marginRight: 6 }} />Pushed viewpoints{selectedViewpoint ? <CheckOutlined style={{ color: '#317A1B', marginLeft: 6 }} /> : null}
                          </div>
                  <div style={{ marginTop: 4 }}>
                    {loadingViewpoints ? (
                      <div style={{ textAlign: 'center', padding: 16 }}>
                        <Spin />
                      </div>
                    ) : viewpoints.length === 0 ? (
                      <div style={{ fontSize: 12.5, color: '#8D8D8D', padding: '8px 0' }}>
                        No viewpoints pushed yet — they appear here when Revit or
                        Navisworks pushes one.
                      </div>
                    ) : (
                      <div style={{
                        maxHeight: 260, overflowY: 'auto',
                        border: '1px solid #EFEFEF', borderRadius: 6, background: '#fff',
                      }}>
                        {viewpoints.map((vp, i) => {
                          const active = selectedViewpoint === vp;
                          return (
                            <div
                              key={vp.issueId || i}
                              onClick={() => setSelectedViewpoint(active ? null : vp)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                padding: '8px 10px', cursor: 'pointer',
                                borderBottom: '1px solid #EFEFEF',
                                background: active ? '#E9F1F5' : '#fff',
                              }}
                            >
                              {vp.screenshot ? (
                                <img
                                  src={previewSrc(vp.screenshot)}
                                  alt={vp.label || 'viewpoint'}
                                  style={{ width: 64, height: 44, objectFit: 'cover', borderRadius: 4, background: '#1a1a1a', flexShrink: 0 }}
                                />
                              ) : (
                                <div style={{
                                  width: 64, height: 44, borderRadius: 4, background: '#F3F4F5',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                  <CameraOutlined style={{ color: '#AEAEAE' }} />
                                </div>
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 12.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {vp.label || 'Viewpoint'}
                                </div>
                                <div style={{ fontSize: 11, color: '#8D8D8D' }}>
                                  {vp.channel || ''}
                                </div>
                              </div>
                              <Tag color={vp.kind === '3d' ? 'purple' : undefined} style={{ margin: 0, ...(vp.kind === '3d' ? {} : { background: '#E9F1F5', borderColor: '#D4E3EC', color: '#3A5765' }) }}>
                                {vp.kind === '3d' ? '3D viewpoint' : '2D viewpoint'}
                              </Tag>
                              {active && <CheckOutlined style={{ color: '#317A1B' }} />}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                        </div>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </div>
        </div>
      </div>
    </>
  );
}
